import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  VStack,
  Text,
  HStack,
  Field,
  Separator,
} from '@chakra-ui/react';
import { RiftboundButton } from '../ui/button';
import { RiftboundSegmentedControl } from '../ui/segmented-control';
import { PasswordField } from '../auth/PasswordField';
import { SignInButtons } from './SignInButtons';
import { devEnv } from '../../constants/devControls';
import { RiftboundInput } from '../ui/input';

interface EmailPasswordFormProps {
  onSignIn: (provider: string) => void;
  onEmailSignIn: (email: string, password: string) => Promise<void>;
  onEmailSignUp: (email: string, password: string, name: string) => Promise<void>;
  onForgotPassword: (email: string) => Promise<void>;
  /**
   * Mobile full-screen covers lead with OAuth; desktop dialog keeps email first.
   */
  providersFirst?: boolean;
}

export const EmailPasswordForm: React.FC<EmailPasswordFormProps> = ({
  onSignIn,
  onEmailSignIn,
  onEmailSignUp,
  onForgotPassword,
  providersFirst = false,
}) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotLoading, setIsForgotLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const passwordReqs = useMemo(() => {
    const minLength = password.length >= 6;
    const uppercase = /[A-Z]/.test(password);
    const special = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
    return { allValid: minLength && uppercase && special };
  }, [password]);

  const handleSegmentChange = useCallback((value: 'signin' | 'register') => {
    setIsSignUp(value === 'register');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setError('');
    setSuccess('');
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (isSignUp && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (isSignUp && !name.trim()) {
      setError('Name is required');
      return;
    }
    setIsLoading(true);
    try {
      if (isSignUp) await onEmailSignUp(email, password, name);
      else await onEmailSignIn(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  }, [isSignUp, password, confirmPassword, name, email, onEmailSignIn, onEmailSignUp]);

  const handleForgotPassword = useCallback(async () => {
    if (!email) {
      setError('Enter your email first');
      return;
    }
    setIsForgotLoading(true);
    setError('');
    setSuccess('');
    try {
      await onForgotPassword(email);
      setSuccess('Password reset email sent — check your inbox.');
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setIsForgotLoading(false);
    }
  }, [email, onForgotPassword]);

  const segmentItems = useMemo(
    () =>
      [
        { value: 'signin', label: 'Sign In' },
        { value: 'register', label: 'Register' },
      ] as const,
    [],
  );

  const statusMessages = (
    <>
      {error ? (
        <Box p={3} bg="red.500/15" borderRadius="md" w="full">
          <Text fontSize="sm" color="red.300">{error}</Text>
        </Box>
      ) : null}
      {success ? (
        <Box p={3} bg="green.500/15" borderRadius="md" w="full">
          <Text fontSize="sm" color="green.300">{success}</Text>
        </Box>
      ) : null}
    </>
  );

  const providerBlock = <SignInButtons onSignIn={onSignIn} />;

  const forgotPasswordControl = !isSignUp ? (
    <RiftboundButton
      type="button"
      treatment="ghost"
      size="sm"
      h="auto"
      minH="unset"
      px={0}
      py={0}
      color={email ? 'accent' : 'textMuted'}
      disabled={!email || isForgotLoading}
      onClick={() => { void handleForgotPassword(); }}
      fontWeight="normal"
    >
      {isForgotLoading ? 'Sending…' : 'Forgot password?'}
    </RiftboundButton>
  ) : null;

  const emailForm = (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <VStack gap={3} w="full" align="stretch">
        {!providersFirst ? statusMessages : null}

        <RiftboundSegmentedControl
          w="full"
          value={isSignUp ? 'register' : 'signin'}
          onValueChange={handleSegmentChange}
          items={segmentItems}
        />

        {isSignUp ? (
          <Field.Root w="full" gap={1.5}>
            <Field.Label fontSize="sm" fontWeight="medium" mb={0}>
              Display name
            </Field.Label>
            <RiftboundInput
              w="full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Summoner_42"
              autoComplete="nickname"
            />
          </Field.Root>
        ) : null}

        <Field.Root w="full" gap={1.5}>
          <Field.Label fontSize="sm" fontWeight="medium" mb={0}>
            Email
          </Field.Label>
          <RiftboundInput
            w="full"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
          />
        </Field.Root>

        <PasswordField
          label="Password"
          value={password}
          onChange={setPassword}
          placeholder="Enter your password"
          autoComplete={isSignUp ? 'new-password' : 'current-password'}
          showStrengthHints={isSignUp}
          labelTrailing={forgotPasswordControl}
        />

        {isSignUp ? (
          <PasswordField
            label="Confirm password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            placeholder="Confirm password"
            autoComplete="new-password"
            matchAgainst={password}
          />
        ) : null}

        <RiftboundButton
          type="submit"
          w="full"
          treatment="primary"
          loading={isLoading}
          disabled={isLoading || (isSignUp && !passwordReqs.allValid)}
          mt={1}
        >
          {isSignUp ? 'Create Account' : 'Sign In'}
        </RiftboundButton>
      </VStack>
    </form>
  );

  const debugBlock = devEnv.hasDebugSignIn() ? (
    <VStack gap={2} w="full" pt={1}>
      <Separator borderColor="yellow.600/40" />
      <Text fontSize="2xs" color="yellow.600" fontWeight="semibold" letterSpacing="wider" textAlign="center">
        DEV ONLY
      </Text>
      <RiftboundButton
        treatment="secondary"
        size="sm"
        w="full"
        onClick={async () => {
          setError('');
          setIsLoading(true);
          try {
            await onEmailSignIn(devEnv.debugUserEmail!, devEnv.debugUserPassword!);
          } catch (err) {
            setError(
              err instanceof Error && err.message.includes('user-not-found')
                ? 'Debug user not found — run: npm run seed:debug'
                : err instanceof Error
                  ? err.message
                  : 'Dev login failed',
            );
          } finally {
            setIsLoading(false);
          }
        }}
        loading={isLoading}
      >
        Sign in as Debug User
      </RiftboundButton>
      <Text fontSize="2xs" color="textMuted" textAlign="center">
        {devEnv.debugUserEmail}
      </Text>
    </VStack>
  ) : null;

  if (providersFirst) {
    return (
      <VStack gap={4} w="full" align="stretch">
        {statusMessages}
        {providerBlock}
        <HStack w="full" gap={3} align="center">
          <Separator flex="1" />
          <Text fontSize="xs" color="textMuted" flexShrink={0}>
            or use email
          </Text>
          <Separator flex="1" />
        </HStack>
        {emailForm}
        {debugBlock}
      </VStack>
    );
  }

  return (
    <VStack gap={4} w="full" align="stretch">
      {emailForm}
      <HStack w="full" gap={3} align="center">
        <Separator flex="1" />
        <Text fontSize="xs" color="textMuted" flexShrink={0}>
          Or continue with
        </Text>
        <Separator flex="1" />
      </HStack>
      {providerBlock}
      {debugBlock}
    </VStack>
  );
};
